import { z } from "zod";
import { healthBehaviourSchema } from "./health-behaviours.js";
/**
 * Confounders: a member's structured acknowledgement that a reading dipped for
 * a reason outside their control (a heatwave, illness, a sick child, travel).
 *
 * A confounder is written as a `self_report.confounder` signal and read every
 * tick by the Heartbeat ConfounderLens, which denies the affected behaviour
 * families and softens tone for a member-chosen window. It never touches the
 * numbers (baseline, trend, snapshot): the member did sleep badly; that is
 * still true. The point is to stop nagging a fix that cannot help.
 *
 * This module is the single source of truth for the reason vocabulary, the
 * reason to families matrix, the duration presets, and the active-window
 * check. It is pure (no clock reads; callers pass `now`) and free of any
 * platform or client-data dependency so the API and both clients share it.
 */
export const confounderReasonSchema = z.enum([
    "heat",
    "illness",
    "caregiving",
    "travel",
    "noise",
    "other",
]);
/**
 * Reason to the behaviour families it plausibly disrupts. Whole-body reasons
 * (illness, travel) spread wide so the member is not re-nagged pillar by
 * pillar; narrow ones stay tight. "other" is deliberately absent: it has no
 * fixed spread, so `familiesForConfounder` resolves it per call from the
 * fallback families the caller supplies (the member's focus, or a card's pillar).
 *
 * These are product and clinical judgements, tunable before ship.
 */
export const CONFOUNDER_FAMILY_MAP = {
    heat: ["sleep", "movement"],
    illness: ["sleep", "movement", "food", "stress"],
    caregiving: ["sleep", "stress"],
    travel: ["sleep", "movement", "food"],
    noise: ["sleep"],
};
/**
 * Last-resort family for an "other" confounder when the caller supplies no
 * fallback (the member has no focus set and there is no card context). Tunable.
 */
export const CONFOUNDER_OTHER_DEFAULT_FAMILY = "sleep";
/**
 * The behaviour families a confounder should quiet. Every reason except "other"
 * uses the fixed matrix. "other" has no inherent spread, so it uses the
 * caller-supplied `fallbackFamilies` (deduped): the member's focus behaviours on
 * the proactive path, or the acknowledged card's pillar on the reactive path.
 * Empty falls back to CONFOUNDER_OTHER_DEFAULT_FAMILY.
 */
export function familiesForConfounder(reason, opts = {}) {
    if (reason === "other") {
        const deduped = Array.from(new Set(opts.fallbackFamilies ?? []));
        return deduped.length > 0 ? deduped : [CONFOUNDER_OTHER_DEFAULT_FAMILY];
    }
    return [...CONFOUNDER_FAMILY_MAP[reason]];
}
/**
 * Member-facing reason labels, in the wording the log form and the timeline both
 * show. Single source so the two surfaces cannot drift; keep them plain and free
 * of em-dashes.
 */
export const CONFOUNDER_REASON_LABELS = {
    heat: "Heat",
    illness: "Illness",
    caregiving: "Caring for someone",
    travel: "Travel",
    noise: "Noise or disruption",
    other: "Something else",
};
export const confounderDurationSchema = z.enum(["one_night", "few_nights", "ongoing"]);
/** Member-facing duration labels, shared by the log form and the timeline. */
export const CONFOUNDER_DURATION_LABELS = {
    one_night: "Just last night",
    few_nights: "A few nights",
    ongoing: "Ongoing",
};
/** Per-reason default duration. Heat and travel span nights; noise is a one-off. */
export const CONFOUNDER_DEFAULT_DURATION = {
    heat: "few_nights",
    illness: "few_nights",
    caregiving: "few_nights",
    travel: "few_nights",
    noise: "one_night",
    other: "one_night",
};
/** Hours a "one night" confounder stays active: through the next morning. */
export const CONFOUNDER_ONE_NIGHT_HOURS = 18;
/** Days a "few nights" confounder stays active. */
export const CONFOUNDER_FEW_NIGHTS_DAYS = 3;
/**
 * Hard cap on any confounder, including "ongoing". A member who forgets to
 * clear an open-ended confounder stops being suppressed after this, rather
 * than having guidance silenced forever.
 */
export const CONFOUNDER_MAX_ACTIVE_DAYS = 7;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
/**
 * Resolve a duration preset to an `effectiveThrough` timestamp (ISO), or null
 * for "ongoing" (open-ended; the active-window check still hard-caps it).
 * `from` is the acknowledgement time.
 */
export function effectiveThroughFor(duration, from) {
    if (duration === "ongoing")
        return null;
    const ms = duration === "one_night"
        ? CONFOUNDER_ONE_NIGHT_HOURS * HOUR_MS
        : CONFOUNDER_FEW_NIGHTS_DAYS * DAY_MS;
    return new Date(from.getTime() + ms).toISOString();
}
/**
 * Whether a confounder acknowledged at `occurredAt` is still inside the hard
 * cap at `now`. This is the backstop, separate from the chosen window: past it,
 * an edit is futile, because a re-saved window recomputes from the original
 * acknowledgement, which the cap has already closed. The editable list uses this
 * to drop a lapsed confounder rather than offer a no-op edit.
 */
export function isConfounderWithinCap(occurredAt, now) {
    return now.getTime() - occurredAt.getTime() <= CONFOUNDER_MAX_ACTIVE_DAYS * DAY_MS;
}
/**
 * True when a confounder written at `occurredAt` is still active at `now`.
 * Enforces the hard cap for open-ended ("ongoing", null effectiveThrough)
 * confounders so a forgotten one eventually stops suppressing.
 */
export function isConfounderActive(args, now) {
    if (!isConfounderWithinCap(args.occurredAt, now))
        return false;
    if (args.effectiveThrough === null)
        return true;
    const through = Date.parse(args.effectiveThrough);
    if (Number.isNaN(through))
        return false;
    return now.getTime() <= through;
}
/**
 * Payload of a `self_report.confounder` signal. `affectedFamilies` is stamped
 * at write time from `familiesForConfounder` so the lens reads the resolved
 * list rather than recomputing it. `effectiveThrough` is null for "ongoing".
 */
export const confounderPayloadSchema = z.object({
    reason: confounderReasonSchema,
    // The member's chosen duration preset, stored so the timeline can show it and
    // the edit form can restore it without reconstructing from the window.
    // Optional so recovery never fails on a confounder written before it was
    // stamped; new writes always include it.
    duration: confounderDurationSchema.optional(),
    affectedFamilies: z.array(healthBehaviourSchema).min(1),
    effectiveThrough: z.string().datetime().nullable(),
    decisionId: z.string().optional(),
    microstepId: z.string().optional(),
    sourceSurface: z.enum(["today_card", "add_to_today"]),
});
//# sourceMappingURL=confounders.js.map